import { useRef } from "react";
import { toast } from "sonner";

/**
 * Deep-clone-snapshot undo history for the FloorPlan2D editor. We push
 * snapshots BEFORE finalised edits (drops, deletes, type-changes, keyboard
 * ops, and the START of a drag). During an active drag we DON'T push —
 * that would record every preview frame.
 *
 * Extracted from FloorPlan2D.jsx (V2.0.18). Returns { pushHistory, undo }.
 */
const MAX_HISTORY = 30;

export default function useUndoHistory(blueprint, onUpdateBlueprint) {
  const historyRef = useRef([]);

  const pushHistory = () => {
    if (!blueprint) return;
    try {
      historyRef.current.push(JSON.parse(JSON.stringify(blueprint)));
      if (historyRef.current.length > MAX_HISTORY) historyRef.current.shift();
    } catch {
      // Defensive — if the blueprint can't be serialised we just skip
      // the snapshot; the UI keeps working.
    }
  };

  const undo = () => {
    if (historyRef.current.length === 0) {
      toast.message("Nothing to undo");
      return;
    }
    const prev = historyRef.current.pop();
    if (prev && onUpdateBlueprint) {
      onUpdateBlueprint(prev);
      toast.success("Undone");
    }
  };

  /** Pop the most-recently pushed snapshot without applying it.
   *  Used to roll back a snapshot that was eagerly pushed for a drag we
   *  ended up bailing on (so undo doesn't surface a no-op step). */
  const popHistory = () => {
    historyRef.current.pop();
  };

  return { pushHistory, undo, popHistory };
}
